import {defineStore} from "pinia";
import EventEmitter from "events";
import {socketStore} from "./socket";
import {crossbarStore} from "./crossbar";
import {apiStore} from "./api";

export const measurementStore = defineStore("measurement", {
    state: () => ({
        status: [],
        initDone: false,
        coconIp: "not found",
        browserIp: "not found",
        measurementDate: undefined,
    }),

    getters: {
        socketConnected() {
            return socketStore().isConnected;
        },

        isInShop() {
            return crossbarStore().isInShop;
        }
    },

    actions: {
        init() { // called in main.js
            this.emitter = new EventEmitter();
            socketStore().onSocketConnected(() => this.onSocketConnected());
            socketStore().onSocketDisconnected(() => this.onSocketDisconnected());
            crossbarStore().on("connected", () => this.onCrossbarConnected());
        },

        onSocketConnected() {
            if (!this.initDone) {
                this.initDone = true;

                socketStore().on("COCON_STATUS", data => this.onCoconStatus(data));
                socketStore().on("MOVIE_COMPLETED", () => this.onMovieCompleted());
                socketStore().on("APPOINTMENT_UPDATED", () => this.onAppointmentUpdated());
                socketStore().on("BED_NOT_READY", () => this.onBedNotReady());
            }
        },

        onSocketDisconnected() {
            this.initDone = false;
            socketStore().removeAllListeners("COCON_STATUS");
            socketStore().removeAllListeners("MOVIE_COMPLETED");
            socketStore().removeAllListeners("APPOINTMENT_UPDATED");
            socketStore().removeAllListeners("BED_NOT_READY");
        },

        async onCrossbarConnected() {
            await this.checkIfInStore();
            crossbarStore().onMessage("cocon-command-" + this.shopId, data => this.crossbarMessage(data));
        },

        pingCoconService() {
            if (crossbarStore().isInShop) {
                crossbarStore().publish("APP_CONNECTED", this.shopId);
            }
        },

        //check ip of the cocon, check ip of the web app & see if they match
        async checkIfInStore() {
            this.doInShopCheck();
            clearInterval(this.shopInShopCheckInterval);
            this.shopInShopCheckInterval = setInterval(() => {
                this.doInShopCheck();
            }, 5000);
        },

        async doInShopCheck() {
            try {
                const appIp = await apiStore().getIp();
                this.browserIp = appIp;

                const coconIp = await crossbarStore().session.call("getCoconIp" + this.shopId);
                this.coconIp = coconIp;
                crossbarStore().isInShop = coconIp === appIp;
                console.log("check if in store", coconIp, appIp, crossbarStore().isInShop);
                clearInterval(this.shopInShopCheckInterval);

                crossbarStore().publish("APP_CONNECTED", this.shopId);
            } catch (error) {
                console.log("crossbar error", error);
            }
        },

        crossbarMessage(data) {
            if (!this.isInShop || this.socketConnected) return;

            if (data.command === "COCON_STATUS") {
                this.checkStatusData(data.status);

            } else if (data.command === "MOVIE_COMPLETED") {
                this.onMovieCompleted();

            } else if (data.command === "APPOINTMENT_UPDATED") {
                this.onAppointmentUpdated();

            } else if (data.command === "BED_NOT_READY") {
                this.onBedNotReady();
            } else if (data.command === "SERVICE_CONNECTED") {
                this.checkIfInStore();
            }
        },

        onCoconStatus(statusData) {
            this.checkStatusData(statusData);
        },

        onMovieCompleted() {
            this.emit("MOVIE_COMPLETED");
        },

        onAppointmentUpdated() {
            this.emit("APPOINTMENT_UPDATED");
        },

        onBedNotReady() {
            this.emit("BED_NOT_READY");
        },

        checkStatusData(statusData) {
            this.status = statusData;

            this.status.forEach(async s => {
                if (s.appointment_id !== null && s.measurement_result !== null) {
                    await apiStore().saveMeasurementResult(s.appointment_id, s.measurement_result);
                    this.confirmMeasurement(s.appointment_id);
                    this.emit("measurement_saved");
                }

                if (s.appointment_id !== null && s.movie_completed === true) {
                    await apiStore().saveMovieCompleted(s.appointment_id);
                    this.confirmMovieCompleted(s.appointment_id);
                    this.emit("movie_completed");
                }
            });

            clearTimeout(this.statusTimeout);
            this.statusTimeout = setTimeout(() => {
                this.status = [];

                if (this.isInShop) {
                    crossbarStore().reconnect();
                }
            }, 3000);

            this.emit("COCON_STATUS", this.status);
        },

        startMeasurement(data) {
            for (let client of data.appointment.clients) { // for auto print, otherwise the date is not set yet
                client.measurement_date = new Date().toISOString().split("T")[0];
            }

            if (socketStore().isConnected) {
                socketStore().emit("START_MOVIE", data);

            } else if (this.isInShop) {
                crossbarStore().publish("START_MOVIE", this.shopId, data);
            }
        },

        stopMeasurement(cocon) {
            if (socketStore().isConnected) {
                socketStore().emit("STOP_MOVIE", {cocon});

            } else if (this.isInShop) {
                crossbarStore().publish("STOP_MOVIE", this.shopId, {cocon});
            }
        },

        confirmMeasurement(appointmentId) {
            if (socketStore().isConnected) {
                socketStore().emit("MEASUREMENT_SAVED_CONFIRMED", {appointment_id:appointmentId});

            } else if (this.isInShop) {
                crossbarStore().publish("MEASUREMENT_SAVED_CONFIRMED", this.shopId, {appointment_id:appointmentId});
            }
        },

        confirmMovieCompleted(appointmentId) {
            if (socketStore().isConnected) {
                socketStore().emit("MOVIE_COMPLETED_CONFIRMED", {appointment_id:appointmentId});

            } else if (this.isInShop) {
                crossbarStore().publish("MOVIE_COMPLETED_CONFIRMED", this.shopId, {appointment_id:appointmentId});
            }
        },

        printFiche(client, locale) {
            if (socketStore().isConnected) {
                socketStore().emit("PRINT_CLIENT", {client, locale});

            } else if (this.isInShop) {
                crossbarStore().publish("PRINT_CLIENT", this.shopId, {client, locale});
            }
        },

        setVolume(cocon, volume) {
            if (socketStore().isConnected) {
                socketStore().emit("CHANGE_VOLUME", {volume, cocon});

            } else if (this.isInShop) {
                crossbarStore().publish("CHANGE_VOLUME", this.shopId, {volume, cocon});
            }
        },

        shutdownCocon() {
            if (socketStore().isConnected) {
                socketStore().emit("SHUTDOWN_COMPUTER");

            } else if (this.isInShop) {
                crossbarStore().publish("SHUTDOWN_COMPUTER", this.shopId);
            }
        },

        emitAppointmentsSync() {
            if (socketStore().isConnected) {
                socketStore().emit("APPOINTMENT_UPDATED");

            } else if (this.isInShop) {
                crossbarStore().publish("APPOINTMENT_UPDATED", this.shopId, {});
            }
        },

        on(command, callback) {
            this.emitter.on(command, callback);
        },

        off(command, callback) {
            this.emitter.off(command, callback);
        },

        emit(command, data) {
            this.emitter.emit(command, data);
        }
    }
});